import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Table } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Select } from '../../components/ui/select';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EnquiryManagement = ({ onUpdate }) => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const fetchEnquiries = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/enquiries`, {
        withCredentials: true
      });
      setEnquiries(response.data);
    } catch (error) {
      toast.error('Failed to fetch enquiries');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (enquiryId, newStatus) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/enquiries/${enquiryId}/status`,
        null,
        {
          params: { status: newStatus },
          withCredentials: true
        }
      );
      toast.success('Status updated');
      fetchEnquiries();
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteEnquiry = async (enquiryId) => {
    if (!window.confirm('Delete this enquiry?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/enquiries/${enquiryId}`, {
        withCredentials: true
      });
      toast.success('Enquiry deleted');
      fetchEnquiries();
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to delete enquiry');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      new: 'bg-blue-100 text-blue-800',
      contacted: 'bg-yellow-100 text-yellow-800',
      enrolled: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return variants[status] || variants.new;
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-purple-200">
              <th className="text-left p-3 font-semibold text-gray-700">Name</th>
              <th className="text-left p-3 font-semibold text-gray-700">Email</th>
              <th className="text-left p-3 font-semibold text-gray-700">Phone</th>
              <th className="text-left p-3 font-semibold text-gray-700">Grade</th>
              <th className="text-left p-3 font-semibold text-gray-700">Curriculum</th>
              <th className="text-left p-3 font-semibold text-gray-700">Status</th>
              <th className="text-left p-3 font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {enquiries.map((enquiry) => (
              <tr key={enquiry.enquiry_id} className="border-b border-gray-100 hover:bg-purple-50">
                <td className="p-3">{enquiry.name}</td>
                <td className="p-3 text-sm">{enquiry.email}</td>
                <td className="p-3 text-sm">{enquiry.phone}</td>
                <td className="p-3">{enquiry.grade}</td>
                <td className="p-3">{enquiry.curriculum}</td>
                <td className="p-3">
                  <select
                    value={enquiry.status}
                    onChange={(e) => updateStatus(enquiry.enquiry_id, e.target.value)}
                    className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(enquiry.status)}`}
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="enrolled">Enrolled</option>
                    <option value="closed">Closed</option>
                  </select>
                </td>
                <td className="p-3">
                  <Button
                    onClick={() => deleteEnquiry(enquiry.enquiry_id)}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-800 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {enquiries.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No enquiries yet
          </div>
        )}
      </div>
    </div>
  );
};

export default EnquiryManagement;
